import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createContainment } from "../../plate/containment";
import { PLATES } from "../../plate/palettes";
import { nodes, links, MODES } from "../../data/decomposition";
import AccessibleEntry, { DESCRIPTION_ID } from "./AccessibleEntry";
import SheetFurniture from "./SheetFurniture";
import DetailPanel from "./DetailPanel";
import UnplacedBlock from "./UnplacedBlock";
import ReadableSheet from "./ReadableSheet";
import { loadState, advance } from "../../plate/hints";

// Phones get the drawing too: below 700px the sheet becomes a scrolling
// document (see _plate.scss) instead of a pinned overlay. The floor is what a
// small phone reports; under it the model is too cramped to read even scrolled,
// and the readable sheet is the better page. The WebGL and reduced-motion
// gates are unchanged - those are about capability and consent, not size.
export const MIN_PLATE_WIDTH = 360;

export function shouldRenderPlate({ width, reducedMotion, webgl }) {
  return Boolean(width >= MIN_PLATE_WIDTH && !reducedMotion && webgl);
}

// Probed once: creating a WebGL context on every resize event is wasteful.
let webglCache;
function detectWebgl() {
  if (webglCache !== undefined) return webglCache;
  webglCache = probeWebgl();
  return webglCache;
}

function probeWebgl() {
  try {
    const c = document.createElement("canvas");
    return Boolean(c.getContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch (e) {
    return false;
  }
}

function detectReducedMotion() {
  try {
    return Boolean(
      typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch (e) {
    return false;
  }
}

function readEnv() {
  return { width: window.innerWidth, reducedMotion: detectReducedMotion(), webgl: detectWebgl() };
}

// How long a prompt stays up; the CSS fade is the same length.
export const PROMPT_MS = 5000;

export const CANVAS_NAME = "System decomposition of Liz Osborn's work, rotatable drawing";

// `env` is a test seam: it replaces probing so plate mode can be forced in
// jsdom. Production passes nothing and the environment is probed.
export default function Plate({ env: envOverride } = {}) {
  const [probed, setEnv] = useState(readEnv);
  const [failed, setFailed] = useState(false);
  const env = envOverride || probed;
  const [plateKey, setPlateKey] = useState("drawing");
  const [selected, setSelected] = useState(null);
  const [ready, setReady] = useState(false); // first fit has landed: reveal the canvas
  const [version, setVersion] = useState(0); // bumps when containment changes
  const containment = useMemo(() => createContainment(nodes, links), []);
  // The prompt chain (see plate/hints.js). Progress lives in a ref so the graph
  // handlers, created once, always see the latest; `prompt` is what is on screen.
  const chainRef = useRef(null);
  if (chainRef.current === null) chainRef.current = loadState();
  const pointerRef = useRef({ x: 0, y: 0 });
  const [prompt, setPrompt] = useState(null);
  const onScreenRef = useRef(false); // a prompt is up: any other is dropped
  const fire = useCallback((event, where) => {
    const r = advance(chainRef.current, event, { busy: onScreenRef.current });
    chainRef.current = r.state;
    if (r.prompt) {
      onScreenRef.current = true;
      const p = pointerRef.current;
      setPrompt({ ...r.prompt, where, x: p.x, y: p.y });
    }
  }, []);
  const canvasRef = useRef(null);
  const graphRef = useRef(null);
  const paletteRef = useRef(PLATES.drawing);

  const renderPlate = shouldRenderPlate(env) && !failed;
  const palette = PLATES[plateKey];
  paletteRef.current = palette;

  useEffect(() => {
    const onResize = () => {
      setEnv(readEnv());
      if (graphRef.current && canvasRef.current) {
        graphRef.current.resize(canvasRef.current.clientWidth, canvasRef.current.clientHeight);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onNodeClick = useCallback(
    (n) => {
      if (!n) return;
      fire({ type: "open", live: Boolean(n.links && n.links.live) }, "panel");
      if (containment.childCount(n.id)) {
        containment.toggle(n.id);
        if (graphRef.current) {
          graphRef.current.setData(containment.visibleGraph());
          graphRef.current.refresh();
        }
        setVersion((v) => v + 1);
      }
      setSelected(n);
    },
    [containment, fire]
  );

  // Mount the graph only when the plate is allowed to render. jsdom (no WebGL)
  // therefore never reaches createPlateGraph. The graph module (three.js and
  // friends) is loaded on demand, so the readable sheet never downloads it.
  useEffect(() => {
    if (!renderPlate || !canvasRef.current) return undefined;
    let cancelled = false;
    let g = null;
    import("../../plate/graph").then(({ createPlateGraph }) => {
      if (cancelled || !canvasRef.current) return;
      g = createPlateGraph(
        canvasRef.current,
        { nodes, links, containment, visible: containment.visibleGraph() },
        paletteRef.current,
        {
          onNodeClick,
          onNodeHover: (n) => {
            if (g) g.setHot(n || null);
            // A node shows a +n badge while it has children and is closed.
            if (n) {
              const count = containment.childCount(n.id);
              if (count && !containment.isOpen(n.id)) fire({ type: "hover", count }, "cursor");
            }
          },
          onOrbit: () => fire({ type: "orbit" }, "cursor"),
          onNodeDragEnd: () => fire({ type: "nodeDrag" }),
          onBackgroundClick: () => setSelected(null),
          onReady: () => setReady(true),
        }
      );
      graphRef.current = g;
    }).catch(() => {
      // Chunk failed to load, or the graph threw (e.g. WebGL context died after
      // the probe passed): serve the readable sheet rather than a blank page.
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
      if (g) g.destroy();
      graphRef.current = null;
    };
  }, [renderPlate, containment, onNodeClick, fire]);

  // Safety net: if the graph never reports its first fit (zero-size container,
  // an engine that never stops), show the canvas anyway rather than a blank sheet.
  useEffect(() => {
    if (!renderPlate) return undefined;
    const t = setTimeout(() => setReady(true), 3000);
    return () => clearTimeout(t);
  }, [renderPlate]);

  // Remember where the cursor is, in a ref (no re-render), so a prompt can be
  // placed where the action was.
  useEffect(() => {
    if (!renderPlate) return undefined;
    const onMove = (e) => { pointerRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [renderPlate]);

  // The first prompt: the only thing a visitor is told without having done
  // anything. Shown once ever.
  //
  // IT WAITS FOR `ready`, NOT FOR MOUNT. The canvas is hidden until the first
  // fit lands, and building fifty sprite textures plus the warm-up takes real
  // time on a cold load. Fired at mount, the prompt spent most of its five
  // seconds over a blank sheet and was already fading by the time the drawing
  // appeared - so it read as "there is no hint". It now arrives with something
  // to orbit.
  useEffect(() => {
    if (renderPlate && ready) fire({ type: "load" }, "load");
  }, [renderPlate, ready, fire]);

  // Every prompt fades on its own (CSS) and is removed shortly after.
  useEffect(() => {
    if (!prompt) return undefined;
    const t = setTimeout(() => { onScreenRef.current = false; setPrompt(null); }, PROMPT_MS);
    return () => clearTimeout(t);
  }, [prompt]);

  useEffect(() => {
    if (graphRef.current) graphRef.current.setPalette(palette);
  }, [palette]);

  const visible = containment.visibleGraph();
  const placed = nodes.filter((n) => !n.unknown);
  const counts = {
    shown: visible.nodes.length,
    folded: placed.length - visible.nodes.length,
    unplaced: nodes.filter((n) => n.unknown).length,
    queries: links.filter((l) => l.mode === MODES.QUERY).length,
  };
  void version; // re-render trigger only

  // Plate mode has no <main> of its own (the drawing is a canvas), so the page
  // element is the <main>. In fallback the readable sheet carries it instead.
  const Page = renderPlate ? "main" : "div";
  return (
    <Page className={renderPlate ? "plate-page plate-page--locked" : "plate-page"}>
      {/* FIRST in the DOM: first in focus order. CSS draws the link at the bottom. */}
      <AccessibleEntry plate={renderPlate} />

      {renderPlate ? null : <ReadableSheet reason />}

      {/* The drawing, only when it is really mounted. A canvas is invisible to
          assistive technology, so the container is named and points at the
          described visual. In fallback neither exists: a node with
          zero size is still in the accessibility tree. */}
      {renderPlate ? (
        <div
          ref={canvasRef}
          className={ready ? "plate-canvas is-ready" : "plate-canvas"}
          role="img"
          aria-label={CANVAS_NAME}
          aria-describedby={DESCRIPTION_ID}
          data-testid="plate-canvas"
        />
      ) : null}

      {renderPlate ? (
        <>
          <SheetFurniture
            palette={palette}
            plateKey={plateKey}
            onPlateChange={setPlateKey}
            counts={counts}
            credentialsHref="/cv"
            onReset={() => graphRef.current && graphRef.current.reset()}
          >
            <UnplacedBlock nodes={nodes} palette={palette} onSelect={setSelected} />
          </SheetFurniture>
          {prompt ? (
            <div key={prompt.id} className={`plate-hint plate-hint--${prompt.where}`} aria-hidden="true"
                 style={prompt.where === "cursor" ? { left: prompt.x + 14, top: prompt.y + 16 } : undefined}>
              {prompt.text}
            </div>
          ) : null}
          {selected ? (
            <DetailPanel
              node={selected}
              onClose={() => setSelected(null)}
              childCount={containment.childCount(selected.id)}
              isOpen={containment.isOpen(selected.id)}
            />
          ) : null}
        </>
      ) : null}
    </Page>
  );
}
